import {Button} from '@wandb/components';
// The new icons do not support the required colors for this design
// So we're using the deprecated icon for now because we can assign colors directly
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';

import {envIsPublicCloud} from '../../config';
import {recordRumAction} from '../../util/datadog/datadogRum';
import {
  DISMISSED_THRESHOLD_MS,
  POLL_INTERVAL_MS,
  THRESHOLD_MS,
} from './defaults';

export function checkSessionAge(
  thresholdMs: number,
  startDate: number,
  callback: (triggerRefresh: boolean) => void
) {
  return (timeNow: number) => {
    const timeDiffMs = timeNow - startDate;
    const shouldTriggerRefresh = timeDiffMs > thresholdMs;
    if (shouldTriggerRefresh) {
      callback(true);
    }
  };
}

/**
 * A self-contained refresh alert component that monitors session age.
 *
 * Renders a portal at the top of the screen when the app version has changed,
 * prompting the user to refresh. Handles all visibility and behavior internally.
 *
 * Examples:
 *   // In App.tsx - just mount the component, no props needed
 *   <RefreshAlert />
 */
export const RefreshAlertSaas: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [anchorTime, setAnchorTime] = useState(Date.now());
  const [hasDismissed, setHasDismissed] = useState(false);

  // Long-polling timer to check for version changes
  useEffect(() => {
    const threshold = hasDismissed ? DISMISSED_THRESHOLD_MS : THRESHOLD_MS;
    const checkSession = checkSessionAge(
      threshold, // 2 days initially, 12 hours after dismiss
      anchorTime, // this date is set when the JS bundle loads
      setIsVisible // triggers the alert
    );
    // Set up polling interval
    const intervalId = setInterval(() => {
      checkSession(Date.now());
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [anchorTime, hasDismissed]);

  const onDismiss = useCallback(() => {
    setIsVisible(false);
    setAnchorTime(Date.now());
    setHasDismissed(true);
    recordRumAction('refresh_browser_alert', {
      status: 'dismissed',
    });
  }, []);

  useEffect(() => {
    if (isVisible) {
      recordRumAction('refresh_browser_alert', {
        status: 'shown',
      });
    }
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return createPortal(
    <Tailwind>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div className="fixed top-60 left-0 right-0 z-[9999] flex justify-center p-16 pointer-events-none">
        <div
          role="alert"
          className="flex items-center gap-16 rounded-lg bg-moon-800 px-16 py-12 shadow-lg pointer-events-auto"
          style={{
            animation: 'fadeIn 300ms ease-in-out',
            animationFillMode: 'both',
          }}>
          <Icon name="reload-refresh" className="text-moon-200" />
          <div className="flex flex-col gap-2 flex-1">
            <p className="text-moon-200 text-base font-semibold m-0">
              Refresh to use the latest version of Weights &amp; Biases.
            </p>
          </div>
          <div className="flex items-center gap-8">
            <Button
              variant="primary"
              size="md"
              onPress={() => {
                recordRumAction('refresh_browser_alert', {
                  status: 'refreshed',
                });
                // Brief delay allows the analytics event to be queued before page reload
                setTimeout(() => window.location.reload(), 50);
              }}>
              Refresh
            </Button>
            <button
              onClick={onDismiss}
              aria-label="Dismiss alert"
              className="hover:bg-moon-700 rounded transition-colors px-8 h-32"
              type="button">
              <Icon name="close" className="text-moon-200" />
            </button>
          </div>
        </div>
      </div>
    </Tailwind>,
    document.body
  );
};

// In non-Saas environments there's no point in this alert since FE and BE versions can't become disconnected
const RefreshAlertNull = () => null;

export const RefreshAlert = envIsPublicCloud
  ? RefreshAlertSaas
  : RefreshAlertNull;
